import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, Eye, EyeOff, ShieldCheck, Leaf } from "lucide-react";
import { toast } from "sonner";
import { hasRole, INTERNAL_ROLES } from "@/lib/roles";

export default function ChangePassword() {
  const navigate = useNavigate();
  const { user, refresh, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next1, setNext1] = useState("");
  const [next2, setNext2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const forced = Boolean(user?.must_change_password);

  const minLen = hasRole(user, ...INTERNAL_ROLES) ? 12 : 8;
  const rules = [
    { ok: next1.length >= minLen, txt: `Almeno ${minLen} caratteri` },
    { ok: /[a-z]/.test(next1), txt: "Una lettera minuscola" },
    { ok: /[A-Z]/.test(next1), txt: "Una lettera maiuscola" },
    { ok: /\d/.test(next1), txt: "Una cifra" },
    { ok: next1 === next2 && next1.length > 0, txt: "Le password coincidono" },
    { ok: next1.length > 0 && next1 !== current, txt: "Diversa dalla password attuale" },
  ];
  const allOk = rules.every((r) => r.ok);

  async function submit(e) {
    e.preventDefault();
    if (!allOk) return;
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next1,
      });
      toast.success("Password aggiornata con successo");
      await refresh();
      navigate("/app", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setLoading(false); }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-viva-50 via-white to-slate-50 flex items-center justify-center p-6" data-testid="change-password-page">
      <Card className="max-w-md w-full p-8 rounded-2xl shadow-soft border-slate-100">
        <div className="flex items-center gap-2 text-viva-700 text-xs font-bold uppercase tracking-[0.18em] mb-4">
          <Leaf className="w-4 h-4" /> VIVA ESG
        </div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-viva-50 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-viva-600" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">
              {forced ? "Imposta la tua password" : "Cambia password"}
            </h1>
            <p className="text-xs text-slate-500">
              {forced
                ? "Al primo accesso devi sostituire la password temporanea."
                : "Aggiorna la tua password di accesso."}
            </p>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4 mt-6">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              {forced ? "Password temporanea" : "Password attuale"}
            </Label>
            <Input
              type={show ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              data-testid="cp-current"
              required
              className="mt-1.5 h-11 rounded-lg"
              autoComplete="current-password"
            />
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nuova password</Label>
            <div className="relative mt-1.5">
              <Input
                type={show ? "text" : "password"}
                value={next1}
                onChange={(e) => setNext1(e.target.value)}
                data-testid="cp-new1"
                required
                className="h-11 rounded-lg pr-12"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShow(!show)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              >
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Conferma nuova password</Label>
            <Input
              type={show ? "text" : "password"}
              value={next2}
              onChange={(e) => setNext2(e.target.value)}
              data-testid="cp-new2"
              required
              className="mt-1.5 h-11 rounded-lg"
              autoComplete="new-password"
            />
          </div>

          <ul className="text-xs space-y-1 bg-slate-50 rounded-lg p-3 border border-slate-100" data-testid="cp-rules">
            {rules.map((r, i) => (
              <li key={i} className={`flex items-center gap-2 ${r.ok ? "text-viva-700" : "text-slate-500"}`}>
                <ShieldCheck className={`w-3.5 h-3.5 ${r.ok ? "text-viva-500" : "text-slate-300"}`} />
                {r.txt}
              </li>
            ))}
          </ul>

          <Button
            type="submit" disabled={!allOk || loading} data-testid="cp-submit"
            className="w-full h-11 bg-viva-500 hover:bg-viva-600 text-white font-semibold rounded-lg"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {forced ? "Imposta password e prosegui" : "Aggiorna password"}
          </Button>

          {!forced && (
            <Button
              type="button" variant="ghost"
              onClick={() => navigate(-1)}
              data-testid="cp-cancel"
              className="w-full text-slate-500"
            >
              Annulla
            </Button>
          )}
          {forced && (
            <Button
              type="button" variant="ghost"
              onClick={() => logout()}
              className="w-full text-slate-400 hover:text-slate-600 text-xs"
            >
              Esci dalla sessione
            </Button>
          )}
        </form>
      </Card>
    </div>
  );
}
